import React from 'react'
import Container from './micro-components/Container'
import Title from './micro-components/Title'
import Grid from './micro-components/Grid'
import Rating from './micro-components/Rating'
import RatingChip from './micro-components/Chip'
import GenericContent, { type GenericRow } from './GenericContent'

type PerformanceHighlights =
  | {
      rating?: number
      category?: string
      remarks?: string
      items?: GenericRow[]
    }
  | GenericRow[]

const Highlight: React.FC<{
  performance_highlights: PerformanceHighlights
  summary?: string
  rating?: number
}> = ({ performance_highlights, summary, rating }) => {
  const isArray = Array.isArray(performance_highlights)

  const rows: GenericRow[] = (() => {
    if (isArray) return performance_highlights as GenericRow[]

    const maybeItems = (performance_highlights as any)?.items
    if (Array.isArray(maybeItems) && maybeItems.length) return maybeItems

    const fallbackCriteria =
      (performance_highlights as any)?.criteria ||
      (performance_highlights as any)?.category ||
      'Performance'
    const fallbackRating =
      (performance_highlights as any)?.rating ??
      (typeof rating === 'number' ? rating : '-')
    const fallbackRemarks =
      (performance_highlights as any)?.remarks ??
      summary ??
      ''

    return [
      {
        criteria: fallbackCriteria,
        rating: fallbackRating,
        remarks: fallbackRemarks,
      },
    ]
  })()

  // Rating and summary are provided by parent; default rating to 0 if absent
  const effectiveRating = rating ?? 0

  const chipScore: number | string | undefined =
    typeof rating === 'number'
      ? rating
      : !isArray
        ? (performance_highlights as any)?.rating
        : undefined

  const ratingChipScore =
    chipScore !== undefined &&
    chipScore !== null &&
    `${chipScore}`.trim() !== '' &&
    `${chipScore}`.trim() !== '-'
      ? chipScore
      : undefined

  return (
    <Container className="gap-6 h-full">
      <Title title="Performance Highlights" />
      <div
        className="rounded-[6px] flex flex-row justify-center items-start gap-6 self-stretch p-6 overflow-hidden min-h-[240px] relative"
        style={{
          background: 'linear-gradient(105deg, #F7FBFE 0%, #CEE7FF 100%)',
        }}
      >
        <div className="w-[150px] shrink self-stretch items-center hidden md:flex lg:flex xl:flex 2xl:flex">
          <Grid />
        </div>
        <div className="flex-1 flex flex-col gap-4">
          <Rating rating={effectiveRating} title="Full Interview Score" />
          <div className="flex flex-col gap-3.5">
            {ratingChipScore !== undefined ? (
              <RatingChip score={ratingChipScore} />
            ) : null}
            {summary ? (
              <span className="text-grey-6 font-dm-sans text-[16px] font-normal leading-[24px] tracking-[0.03em]">
                {summary}
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="w-full p-0">
        <GenericContent
          className="p-0 m-0"
          tableSectionClassName="p-0 m-0"
          rows={rows}
          showTitle={false}
        />
      </div>
    </Container>
  )
}

export default Highlight